import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LoadingIcon from "../../assets/LoadingIcon";
import PrimaryButton from "../../components/button/PrimaryButton";
import {
  getSuperAdminTenantConfig,
  uploadSuperAdminTenantLogo,
  updateSuperAdminTenantConfig,
} from "../../services/superadmin-tenants";
import TenantConfigFields, {
  createEmptyTenantConfig,
} from "./TenantConfigFields";

const ACCEPTED_LOGO_TYPES = ".png,.jpg,.jpeg,.webp,.svg";

const TenantConfigEdit = () => {
  const { tenantCode } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(createEmptyTenantConfig());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true);
      try {
        const response = await getSuperAdminTenantConfig(tenantCode);
        setConfig({ ...createEmptyTenantConfig(), ...(response ?? {}) });
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [tenantCode]);

  const updateConfig = (key, value) =>
    setConfig((prev) => ({ ...prev, [key]: value }));

  const handleLogoUpload = async () => {
    if (!logoFile) {
      setError("Please select a logo image to upload.");
      return;
    }

    setUploadingLogo(true);
    setError(null);
    try {
      const response = await uploadSuperAdminTenantLogo(tenantCode, logoFile);
      setConfig((prev) => ({
        ...prev,
        [response.settingKey ?? "branding.logo_path"]: response.settingValue,
      }));
      setLogoUrl(response.logoUrl ?? "");
      setLogoFile(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateSuperAdminTenantConfig(tenantCode, config);
      navigate(`/su/admin/tenants/${tenantCode}`, { replace: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingIcon />;

  return (
    <div className="bg-white rounded shadow p-6">
      <h1 className="uppercase text-xl font-bold text-slate-700 mb-6">
        Tenant Config
      </h1>
      {error ? (
        <div className="bg-errorBg text-errorText text-sm p-4 mb-6 border-errorBorder border-[0.5px] rounded-sm">
          {error}
        </div>
      ) : null}
      <div className="mb-8 rounded bg-neutral-light p-4">
        <h2 className="uppercase text-sm font-bold text-slate-600 mb-4">
          Logo
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-end">
          <div>
            <label className="block uppercase text-slate-600 text-xs font-bold mb-2">
              Tenant Logo
            </label>
            <input
              type="file"
              aria-label="Tenant Logo"
              accept={ACCEPTED_LOGO_TYPES}
              className="block w-full text-sm text-slate-600 file:mr-4 file:rounded file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white"
              onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
            />
            <div className="mt-3 text-xs text-slate-500">
              Current logo path: {config["branding.logo_path"] || "-"}
            </div>
            {logoUrl ? (
              <a
                href={logoUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-block text-xs font-semibold text-primary underline"
              >
                View uploaded logo
              </a>
            ) : null}
          </div>
          <PrimaryButton
            title="Upload Logo"
            onClick={handleLogoUpload}
            isBusy={uploadingLogo}
          />
        </div>
      </div>

      <TenantConfigFields config={config} onChange={updateConfig} />
      <div className="flex justify-end mt-8">
        <PrimaryButton
          title="Save Config"
          onClick={handleSave}
          isBusy={saving}
        />
      </div>
    </div>
  );
};

export default TenantConfigEdit;
